import React, { useState, useEffect } from 'react';
import { NetworkConfig } from '../config/networks';
import { trustlineService } from '../services/trustline';
import './PolicyDescription.css';

interface PolicyDescriptionProps {
  currentNetwork?: NetworkConfig;
  contractAddress?: string;
  isConnected?: boolean;
}

const PolicyDescription: React.FC<PolicyDescriptionProps> = ({ currentNetwork, contractAddress, isConnected }) => {
  const [defaultPolicy, setDefaultPolicy] = useState<any>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);

  // Fetch default policy when network and contract address are available
  useEffect(() => {
    const fetchDefaultPolicy = async () => {
      if (!currentNetwork || !contractAddress) {
        setDefaultPolicy(null);
        return;
      }

      setLoadingPolicy(true);
      try {
        const response = await trustlineService.fetchDefaultPolicyForWithdraw(
          currentNetwork.chainId,
          contractAddress
        );
        
        if (response && 'result' in response && response.result.success) {
          setDefaultPolicy(response.result);
        } else {
          setDefaultPolicy(null);
        }
      } catch (error) {
        console.debug('Failed to fetch default policy:', error);
        setDefaultPolicy(null);
      } finally {
        setLoadingPolicy(false);
      }
    };

    fetchDefaultPolicy();
  }, [currentNetwork, contractAddress]);

  // Extract email domains from default policy
  const getWhitelistedEmailDomains = (): string => {
    if (!defaultPolicy?.policy) {
      return ''; // Default fallback
    }

    const policyData = defaultPolicy.policy;
    if (policyData.type === 'UserAuthenticationPolicy' && policyData.allowedEmailDomains) {
      return policyData.allowedEmailDomains.join(',');
    }

    return ''; // Default fallback
  };

  const whitelistedEmailDomain = getWhitelistedEmailDomains();
  
  // Format emails and domains for display: add @ prefix only to domains, not full emails
  const formatEmailsAndDomains = (emailsAndDomains: string): string => {
    if (!emailsAndDomains) return '';
    return emailsAndDomains
      .split(',')
      .map(item => {
        const trimmed = item.trim();
        // If it's a full email (contains @), don't add @ prefix
        // If it's a domain, add @ prefix
        return trimmed.includes('@') ? trimmed : `@${trimmed}`;
      })
      .join(', ');
  };
  
  return (
    <div className="policy-section">
      <div className="policy-header">
        <h3>Policy</h3>
        {currentNetwork && contractAddress && (
          <a 
            href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="explorer-link"
          >
            View contract on explorer ↗
          </a>
        )}
      </div>
      <div className="policy-content">
        <p className="policy-description">
          Transactions made on{' '}
          {currentNetwork && contractAddress ? (
            <a 
              href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="escrow-link"
            >
              this Safe Transfer Contract
            </a>
          ) : (
            'this Safe Transfer Contract'
          )}
          {' '}are subject to verifications through Trustline trusted providers.
        </p>
        {isConnected && (
          <div className="policy-items">
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">Watch Lists</span>
                <div className="policy-scopes">
                  <span className="policy-scope scope-sender">Sender</span>
                  <span className="policy-scope">Beneficiary</span>
                </div>
              </div>
              <ul className="policy-desc policy-list">
                <li>OFAC Sanctions list</li>
                <li>Community self-reported lists</li>
                <li>Service internal lists</li>
              </ul>
            </div>
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">End-User Verification</span>
                <span className="policy-scope">Beneficiary</span>
              </div>
              <span className="policy-desc">
              <ul className="policy-desc policy-list">
                <li>
                  For all transfers, <strong>Email</strong> verification of the beneficiary, which, by default, needs to belong to:{' '}
                  {loadingPolicy ? (
                    <strong className="policy-loading">Loading...</strong>
                  ) : whitelistedEmailDomain ? (
                    <strong>{formatEmailsAndDomains(whitelistedEmailDomain)}</strong>
                  ) : (
                    <strong>configured email domains</strong>
                  )}
                </li>
                <li>For high amount transfers, <strong>Phone number</strong> verification of beneficary within: <strong>EU or US region</strong>.</li>
              </ul>
              </span>
            </div>
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">Fraud & Hack Prevention</span>
                <div className="policy-scopes">
                  <span className="policy-scope">Beneficiary</span>
                  <span className="policy-scope scope-transaction">Transaction</span>
                </div>
              </div>
              <ul className="policy-desc policy-list">
                <li><strong>Simulate tx impact</strong> on smart contract.</li>
                <li>Check <strong>risk score</strong> for tx wallets and assets.</li>
                <li>Require secondary beneficiary verification for <strong>high amount transfers.</strong></li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyDescription;
